import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBandeau } from '../BandeauContext';
import { apiClient } from '../../utils/apiClient';
import { SubjectIcon } from '../SubjectIcons';
import { generateSlug } from '../../utils/slugUtils';
import TabBar from '../TabBar';
import ActionBtn from '../admin/ActionBtn';
import PlacementTest from './PlacementTest';
import { SkillsList, SkillDetail, SkillExercise, slugify as skillsSlugify, exerciseThemeSlug } from './SkillsView';
import Modal from '../common/Modal';
import CourseContentDisplay from './CourseContentDisplay';
import { BookOpen, Sparkles, PencilLine, Trophy, Lock } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function sortByNotionNumber([, a], [, b]) {
    const na = (a[0]?.notion_number ?? '').split('.').map(Number);
    const nb = (b[0]?.notion_number ?? '').split('.').map(Number);
    for (let i = 0; i < Math.max(na.length, nb.length); i++) {
        const d = (na[i] ?? 0) - (nb[i] ?? 0);
        if (d !== 0) return d;
    }
    return 0;
}

// ── Modal historique de maîtrise d'un niveau ─────────────────────────────
function LevelMasteryModal({ levelId, levelLabel, onClose }) {
    const [points, setPoints] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        const h = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [onClose]);

    useEffect(() => {
        apiClient(`${API_BASE}/get_level_mastery_history.php?level_id=${levelId}`)
            .then(r => r.json())
            .then(d => { if (d.success) setPoints(d.points); })
            .finally(() => setLoading(false));
    }, [levelId]);

    const W = 480, H = 220, PAD = { top: 16, right: 16, bottom: 40, left: 36 };
    const chartW = W - PAD.left - PAD.right;
    const chartH = H - PAD.top - PAD.bottom;

    const renderChart = () => {
        if (!points?.length) return (
            <p className="text-center text-sm text-gray-400 py-8">Aucun historique disponible.</p>
        );

        const toX = i => PAD.left + (points.length === 1 ? chartW / 2 : (i / (points.length - 1)) * chartW);
        const toY = v => PAD.top + chartH - (v / 100) * chartH;

        const overallPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.overall)}`).join(' ');
        const notionPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.notion)}`).join(' ');
        const exercicePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.exercice)}`).join(' ');

        const gridLines = [0, 25, 50, 75, 100];

        const step = Math.max(1, Math.floor(points.length / 5));
        const dateLabels = points
            .map((p, i) => ({ i, date: p.date }))
            .filter((_, i) => i % step === 0 || i === points.length - 1);

        return (
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }}>
                {gridLines.map(v => (
                    <g key={v}>
                        <line x1={PAD.left} y1={toY(v)} x2={PAD.left + chartW} y2={toY(v)}
                            stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
                        <text x={PAD.left - 6} y={toY(v) + 4} textAnchor="end" fontSize="9"
                            fill="currentColor" fillOpacity="0.4">{v}%</text>
                    </g>
                ))}

                <path d={notionPath} fill="none" stroke="#a78bfa" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6" />
                <path d={exercicePath} fill="none" stroke="#34d399" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6" />

                <path
                    d={`${overallPath} L${toX(points.length - 1)},${toY(0)} L${toX(0)},${toY(0)} Z`}
                    fill="#3b82f6" fillOpacity="0.08"
                />
                <path d={overallPath} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

                {points.map((p, i) => (
                    <circle key={i} cx={toX(i)} cy={toY(p.overall)} r={hovered === i ? 5 : 3.5}
                        fill={hovered === i ? '#3b82f6' : '#fff'}
                        stroke="#3b82f6" strokeWidth="2"
                        style={{ cursor: 'pointer', transition: 'r 0.1s' }}
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(null)}
                    />
                ))}

                {hovered !== null && (() => {
                    const p = points[hovered];
                    const x = toX(hovered);
                    const y = toY(p.overall);
                    const bx = Math.min(x - 36, W - PAD.right - 80);
                    return (
                        <g>
                            <line x1={x} y1={PAD.top} x2={x} y2={PAD.top + chartH} stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 2" opacity="0.4" />
                            <rect x={Math.max(PAD.left, bx)} y={y - 46} width="80" height="42" rx="6"
                                fill="currentColor" fillOpacity="0.9" className="text-white dark:text-gray-900" />
                            <text x={Math.max(PAD.left, bx) + 40} y={y - 32} textAnchor="middle" fontSize="11" fontWeight="700" fill="#3b82f6">{p.overall}%</text>
                            <text x={Math.max(PAD.left, bx) + 40} y={y - 19} textAnchor="middle" fontSize="9" fill="#a78bfa">N {p.notion}%</text>
                            <text x={Math.max(PAD.left, bx) + 40} y={y - 8} textAnchor="middle" fontSize="9" fill="#34d399">E {p.exercice}%</text>
                        </g>
                    );
                })()}

                {dateLabels.map(({ i, date }) => (
                    <text key={i} x={toX(i)} y={H - 6} textAnchor="middle" fontSize="9"
                        fill="currentColor" fillOpacity="0.45">{date}</text>
                ))}
            </svg>
        );
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={`Progression — ${levelLabel}`}
        >
            <div className="flex items-center gap-4 mb-4 text-[11px] -mt-2">
                <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-blue-500 rounded inline-block" />Global</span>
                <span className="flex items-center gap-1.5 opacity-60"><span className="w-6 h-0.5 bg-violet-400 rounded inline-block border-dashed" />Notions</span>
                <span className="flex items-center gap-1.5 opacity-60"><span className="w-6 h-0.5 bg-emerald-400 rounded inline-block" />Exercices</span>
            </div>
            {loading
                ? <div className="py-8 text-center text-sm text-gray-400 animate-pulse">Chargement…</div>
                : renderChart()
            }
        </Modal>
    );
}


function CourseView({ user, onExit, defaultSubject, onChapterChange }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { setBandeau, resetBandeau } = useBandeau();

    // Extraction des slugs depuis l'URL (ex: /lecon/maths/calcul-litteral/lecon)
    const pathParts = location.pathname.split('/');
    const subjectSlugParam = pathParts[1] === 'apprendre' && pathParts[2] ? pathParts[2] : null;
    const chapterSlugParam = pathParts[1] === 'apprendre' && pathParts[3] ? pathParts[3] : null;
    const viewTab = pathParts[1] === 'apprendre' && pathParts[4] ? pathParts[4] : null; // 'lecon'|'exercice'|'plus'
    const skillSlugParam = pathParts[1] === 'apprendre' && pathParts[5] ? pathParts[5] : null;
    const themeSlugParam = pathParts[1] === 'apprendre' && pathParts[6] ? pathParts[6] : null;

    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [courseContent, setCourseContent] = useState(null);
    const [lockedNotions, setLockedNotions] = useState({});
    const [notionScores, setNotionScores] = useState({});
    const [levels, setLevels] = useState([]);
    const [masteryThreshold, setMasteryThreshold] = useState(80);
    const [, setCurrentLevel] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLevelLoading, setIsLevelLoading] = useState(false);
    const [openItemsNotionId, setOpenItemsNotionId] = useState(null);
    const [masteryHistoryLevel, setMasteryHistoryLevel] = useState(null); // { levelId, levelLabel }
    const [activeNotionId, setActiveNotionId] = useState(null);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [chapterSkills, setChapterSkills] = useState(null);
    const [chapterSkillsLoading, setChapterSkillsLoading] = useState(false);
    const [detailSkill, setDetailSkill] = useState(null);
    const [detailSkillLoading, setDetailSkillLoading] = useState(false);
    const [adminModal, setAdminModal] = useState(null); // null | {type:'subject'} | {type:'chapter',subjectId,subjectName}
    const [adminBusy, setAdminBusy] = useState(false);
    const [allSubjects, setAllSubjects] = useState([]);
    const [isPlacementTestOpen, setIsPlacementTestOpen] = useState(false);
    const [placementTestLevelIndex, setPlacementTestLevelIndex] = useState(0);
    const [editingSection, setEditingSection] = useState(null);

    useEffect(() => {
        console.log("react_state_log: editingSection state transition to:", editingSection);
    }, [editingSection]);

    const renderEditingSectionModal = () => {
        if (!editingSection) return null;
        return (
            <Modal
                isOpen={true}
                onClose={() => setEditingSection(null)}
                title="Modifier le bloc de cours"
            >
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    setAdminBusy(true);
                    const form = e.target;
                    const titleVal = form.title.value;
                    const typeVal = form.type.value;
                    const contentVal = form.content.value;

                    try {
                        const res = await apiClient(`${API_BASE}/admin_content.php?action=save_course`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: editingSection.id,
                                notion_id: editingSection.notion_id || editingSection.category_id,
                                title: titleVal,
                                type: typeVal,
                                content: contentVal,
                                display_order: editingSection.display_order || 0
                            })
                        });
                        const json = await res.json();
                        if (json.success) {
                            sessionStorage.setItem('scroll_to_section_id', `section-${editingSection.id}`);
                            setEditingSection(null);
                            reloadActiveChapter();
                        } else {
                            alert(json.message || "Erreur lors de la sauvegarde du bloc");
                        }
                    } catch (err) {
                        console.error(err);
                        alert("Erreur lors de la connexion au serveur");
                    } finally {
                        setAdminBusy(false);
                    }
                }} className="space-y-4 text-left">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                            Titre du bloc (optionnel)
                        </label>
                        <input
                            type="text"
                            name="title"
                            defaultValue={editingSection.title || ''}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-black/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-gray-900 dark:text-white transition-all text-sm"
                            placeholder="Ex: Théorème fondamental de l'algèbre..."
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                            Type de bloc
                        </label>
                        <select
                            name="type"
                            defaultValue={editingSection.content_type || editingSection.type || 'text'}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-gray-900 dark:text-white transition-all text-sm"
                        >
                            <option value="text">Texte libre (Markdown)</option>
                            <option value="definition">📘 Définition</option>
                            <option value="theorem">📐 Théorème</option>
                            <option value="proof">⚙️ Démonstration</option>
                            <option value="example">💡 Exemple (avec spoilers)</option>
                            <option value="remark">✍️ Remarque</option>
                            <option value="note">📌 Note</option>
                            <option value="heading">Titre de section (h2/h3)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider">
                            Contenu (Markdown & KaTeX)
                        </label>
                        <textarea
                            name="content"
                            rows={8}
                            defaultValue={editingSection.content || ''}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-black/20 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none text-gray-900 dark:text-white font-mono text-sm leading-relaxed transition-all resize-y"
                            placeholder="Entrez le contenu en Markdown..."
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-white/[0.04] mt-6">
                        <button
                            type="button"
                            onClick={() => setEditingSection(null)}
                            disabled={adminBusy}
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={adminBusy}
                            className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 dark:bg-cyan-600 dark:hover:bg-cyan-500 disabled:bg-gray-300 dark:disabled:bg-gray-800 disabled:text-gray-500 transition-colors shadow-lg shadow-blue-500/10 dark:shadow-cyan-500/10 cursor-pointer"
                        >
                            {adminBusy ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                    </div>
                </form>
            </Modal>
        );
    };

    const reloadActiveChapter = () => {
        if (!selectedChapter) return;
        setIsLoading(true);
        setCourseContent(null);
        levelContentCache.current = {};
        saveLevelCache({});
        apiClient(`${API_BASE}/get_course.php?chapter_id=${selectedChapter.id}`)
            .then(res => res.json())
            .then(data => {
                if (!data.success) { setIsLoading(false); return; }
                setNotionScores(data.notion_scores || {});
                const lvls = data.levels || [];
                const thresh = data.mastery_threshold ?? 80;
                setLevels(lvls);
                setMasteryThreshold(thresh);
                let cur = lvls.length > 0 ? lvls[0].level_number : null;
                for (let i = 0; i < lvls.length; i++) {
                    const accessible = i === 0 || Number(lvls[i - 1].mastery_overall) >= thresh;
                    if (accessible) cur = lvls[i].level_number;
                    else break;
                }
                setCurrentLevel(cur);
                setSelectedLevel(cur);
                if (cur === null) { setIsLoading(false); return; }
                return apiClient(`${API_BASE}/get_course.php?chapter_id=${selectedChapter.id}&level_number=${cur}`)
                    .then(r => r.json())
                    .then(levelData => {
                        if (levelData.success) {
                            levelContentCache.current[`${selectedChapter.id}_${cur}`] = {
                                courseData: levelData.course_data,
                                lockedNotions: levelData.locked_notions || {},
                            };
                            saveLevelCache(levelContentCache.current);
                            setCourseContent(levelData.course_data);
                            setLockedNotions(levelData.locked_notions || {});
                        }
                        setIsLoading(false);
                    });
            })
            .catch(() => setIsLoading(false));
    };

    // Restauration du scroll après modification d'un bloc par un admin
    useEffect(() => {
        if (!isLoading && courseContent) {
            const scrollTo = sessionStorage.getItem('scroll_to_section_id');
            if (scrollTo) {
                setTimeout(() => {
                    const el = document.getElementById(scrollTo);
                    if (el) {
                        el.scrollIntoView({ behavior: 'auto', block: 'center' });
                        // Ajout d'une animation pulse esthétique et temporaire
                        el.classList.add('animate-pulse');
                        setTimeout(() => el.classList.remove('animate-pulse'), 2000);
                    }
                    sessionStorage.removeItem('scroll_to_section_id');
                }, 150);
            }
        }
    }, [isLoading, courseContent]);

    // Notions déjà signalées comme vues (évite les doublons)
    const visitedNotions = useRef(new Set());
    const mainScrollEl = useRef(null);
    // Cache du contenu par niveau persisté en sessionStorage
    const CACHE_KEY = 'axiomus_level_cache';
    const levelContentCache = useRef(null);
    if (!levelContentCache.current) {
        try { levelContentCache.current = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); }
        catch { levelContentCache.current = {}; }
    }
    const saveLevelCache = (cache) => {
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { }
    };

    // Helper : slug d'une matière (priorité au slug DB, fallback generateSlug)
    const subjectUrlSlug = (ch) => ch.subject_slug || generateSlug(ch.subject_name || 'Général');

    // Mise à jour du bandeau selon la vue active
    useEffect(() => {
        if (detailSkill && skillSlugParam && selectedChapter) {
            const subSlug = subjectUrlSlug(selectedChapter);
            const chapSlug = selectedChapter.slug || generateSlug(selectedChapter.name);
            const chapterBase = `/apprendre/${subSlug}/${chapSlug}`;
            const baseCrumbs = [
                { label: 'Apprendre', onClick: () => navigate(`/apprendre/${subSlug}`) },
                { label: selectedChapter.subject_name || 'Général', onClick: () => navigate(`/apprendre/${subSlug}`) },
                { label: selectedChapter.name, onClick: () => navigate(chapterBase) },
                { label: 'Exercices', onClick: () => navigate(`${chapterBase}/exercice`) },
            ];
            if (themeSlugParam) {
                const exIdx = detailSkill.exercises?.findIndex((ex, i) => exerciseThemeSlug(ex, i) === themeSlugParam) ?? -1;
                const activeEx = exIdx >= 0 ? detailSkill.exercises[exIdx] : null;
                const decliTitle = activeEx?.theme || `Déclinaison ${exIdx + 1}`;
                setBandeau({
                    breadcrumb: [...baseCrumbs, { label: detailSkill.title, onClick: () => navigate(`${chapterBase}/exercice/${skillSlugParam}`) }],
                    title: decliTitle,
                });
            } else {
                setBandeau({ breadcrumb: baseCrumbs, title: detailSkill.title });
            }
        } else if (selectedChapter) {
            setBandeau({
                breadcrumb: [
                    { label: 'Apprendre', onClick: () => navigate(`/apprendre/${subjectUrlSlug(selectedChapter)}`) },
                    { label: selectedChapter.subject_name || 'Général', onClick: () => navigate(`/apprendre/${subjectUrlSlug(selectedChapter)}`) },
                ],
                title: selectedChapter.name,
            });
        } else if (subjectSlugParam && chapters.length > 0) {
            const subjectName = chapters.find(c => subjectUrlSlug(c) === subjectSlugParam)?.subject_name;
            setBandeau({
                breadcrumb: [{ label: 'Apprendre', onClick: () => navigate('/apprendre', { state: { ignoreDefaultSubject: true } }) }],
                title: subjectName || 'Matière',
            });
        } else {
            setBandeau({ breadcrumb: null, title: 'Apprendre' });
        }
        return () => resetBandeau();
    }, [selectedChapter, subjectSlugParam, chapters.length, detailSkill, skillSlugParam, themeSlugParam]); // eslint-disable-line react-hooks/exhaustive-deps

    // Redirection automatique vers la matière sélectionnée sur le tableau de bord
    useEffect(() => {
        if (location.pathname === '/apprendre' && defaultSubject && defaultSubject !== 'Toutes' && !location.state?.ignoreDefaultSubject) {
            navigate(`/apprendre/${generateSlug(defaultSubject)}`, { replace: true });
        }
    }, [location.pathname, location.state, defaultSubject, navigate]);

    // Redirection automatique si un ID de chapitre est passé en paramètre depuis le Chatbot
    useEffect(() => {
        const queryChapterId = new URLSearchParams(location.search).get('chapter_id');
        if (queryChapterId && chapters.length > 0) {
            const chapter = chapters.find(c => (c.id && c.id.toString() === queryChapterId) || (c.chapter_id && c.chapter_id.toString() === queryChapterId));
            if (chapter) {
                navigate(`/apprendre/${subjectUrlSlug(chapter)}/${generateSlug(chapter.name || 'Chapitre')}${location.hash}`, { replace: true });
            } else {
                setIsLoading(false);
            }
        }
    }, [location.search, location.hash, chapters, navigate]);

    // Snapshot des paramètres d'URL au montage pour un fetch unique sans dépendances instables
    const initialChapterSlugRef = useRef(chapterSlugParam);
    const initialSearchRef = useRef(location.search);

    // Charger la liste des chapitres au démarrage (une seule fois)
    useEffect(() => {
        apiClient(`${API_BASE}/get_course.php`)
            .then(res => res.json())
            .then(data => {
                if (data.success) setChapters(data.chapters);
                if (!initialChapterSlugRef.current && !new URLSearchParams(initialSearchRef.current).get('chapter_id')) {
                    setIsLoading(false);
                }
            })
            .catch(err => {
                console.error("Erreur de chargement de la bibliothèque:", err);
                setIsLoading(false);
            });
        if (['admin', 'supertuteur'].includes(user?.role)) {
            apiClient(`${API_BASE}/admin_content.php?action=list_subjects`).then(r => r.json()).then(data => {
                if (data.success) setAllSubjects(data.subjects);
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const reloadChapters = () => {
        apiClient(`${API_BASE}/get_course.php`).then(r => r.json()).then(data => {
            if (data.success) setChapters(data.chapters);
        });
        if (['admin', 'supertuteur'].includes(user?.role)) {
            apiClient(`${API_BASE}/admin_content.php?action=list_subjects`).then(r => r.json()).then(data => {
                if (data.success) setAllSubjects(data.subjects);
            });
        }
    };

    // Charger le contenu d'un niveau (avec cache)
    const loadLevelContent = (chapterId, levelNumber) => {
        if (!chapterId || levelNumber === null) return;
        const cacheKey = `${chapterId}_${levelNumber}`;

        if (levelContentCache.current[cacheKey]) {
            const { courseData, lockedNotions: ln } = levelContentCache.current[cacheKey];
            setCourseContent(courseData);
            setLockedNotions(ln);
            setIsLevelLoading(false);
            return;
        }

        setIsLevelLoading(true);
        apiClient(`${API_BASE}/get_course.php?chapter_id=${chapterId}&level_number=${levelNumber}`)
            .then(r => r.json())
            .then(data => {
                if (data.success) {
                    levelContentCache.current[cacheKey] = {
                        courseData: data.course_data,
                        lockedNotions: data.locked_notions || {},
                    };
                    saveLevelCache(levelContentCache.current);
                    setCourseContent(data.course_data);
                    setLockedNotions(data.locked_notions || {});
                }
                setIsLevelLoading(false);
            })
            .catch(() => setIsLevelLoading(false));
    };

    // Surveiller les changements de l'URL pour charger un cours précis
    useEffect(() => {
        if (chapterSlugParam && chapters.length > 0) {
            const chapter = chapters.find(c => generateSlug(c.name) === chapterSlugParam && (c.subject_slug || generateSlug(c.subject_name || 'Général')) === subjectSlugParam);
            if (chapter) {
                setIsLoading(true);
                setCourseContent(null);
                levelContentCache.current = {};
                saveLevelCache({});
                setChapterSkills(null);
                setDetailSkill(null);
                apiClient(`${API_BASE}/get_course.php?chapter_id=${chapter.id}`)
                    .then(res => res.json())
                    .then(data => {
                        if (!data.success) { setIsLoading(false); return; }
                        setNotionScores(data.notion_scores || {});
                        setSelectedChapter(chapter);
                        if (onChapterChange) onChapterChange(chapter);
                        const lvls = data.levels || [];
                        const thresh = data.mastery_threshold ?? 80;
                        setLevels(lvls);
                        setMasteryThreshold(thresh);
                        // Niveau actuel = niveau le plus élevé accessible
                        let cur = lvls.length > 0 ? lvls[0].level_number : null;
                        for (let i = 0; i < lvls.length; i++) {
                            const accessible = i === 0 || Number(lvls[i - 1].mastery_overall) >= thresh;
                            if (accessible) cur = lvls[i].level_number;
                            else break;
                        }
                        setCurrentLevel(cur);
                        setSelectedLevel(cur);
                        if (cur === null) { setIsLoading(false); return; }
                        // Charger le contenu du niveau initial
                        return apiClient(`${API_BASE}/get_course.php?chapter_id=${chapter.id}&level_number=${cur}`)
                            .then(r => r.json())
                            .then(levelData => {
                                if (levelData.success) {
                                    levelContentCache.current[`${chapter.id}_${cur}`] = {
                                        courseData: levelData.course_data,
                                        lockedNotions: levelData.locked_notions || {},
                                    };
                                    saveLevelCache(levelContentCache.current);
                                    setCourseContent(levelData.course_data);
                                    setLockedNotions(levelData.locked_notions || {});
                                }
                                setIsLoading(false);
                            });
                    })
                    .catch(() => setIsLoading(false));
            } else {
                setIsLoading(false); // Le slug n'existe pas
            }
        } else if (!chapterSlugParam) {
            setSelectedChapter(null);
            setCourseContent(null);
            if (onChapterChange) onChapterChange(null);
        }
    }, [chapterSlugParam, subjectSlugParam, chapters]); // eslint-disable-line react-hooks/exhaustive-deps

    // Forcer le défilement fluide (Scroll) à l'arrivée sur une notion
    useEffect(() => {
        if (!isLoading && courseContent && location.hash) {
            // Un court délai permet de s'assurer que le contenu a bien été rendu dans le DOM
            setTimeout(() => {
                const id = location.hash.replace('#', '');
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
        }
    }, [location.hash, isLoading, courseContent]);

    // IntersectionObserver : envoie une visite unique par notion dès qu'elle entre dans le viewport
    useEffect(() => {
        if (!courseContent) return;
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                const nid = entry.target.dataset.notionId;
                if (!nid || visitedNotions.current.has(nid)) return;
                visitedNotions.current.add(nid);
                observer.unobserve(entry.target);
                apiClient(`${API_BASE}/save_notion_view.php`, {
                    method: 'POST',
                    body: JSON.stringify({ notion_id: parseInt(nid) }),
                }).then(r => r.json()).then(d => {
                    if (d.success && d.knowledge_score != null) {
                        setNotionScores(prev => ({ ...prev, [nid]: d.knowledge_score }));
                    }
                }).catch(() => { });
            });
        }, { threshold: 0.2 });
        document.querySelectorAll('[data-notion-id]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, [courseContent, selectedLevel]);

    // Initialise la ref vers l'élément scrollable une seule fois
    useEffect(() => {
        mainScrollEl.current = document.querySelector('main') ?? window;
    }, []);

    // Suivi de la notion active pour la sidebar (avec debounce)
    useEffect(() => {
        if (!courseContent) return;
        const OFFSET = 140;
        let rafId = null;
        const update = () => {
            const els = document.querySelectorAll('[data-notion-id]');
            if (!els.length) return;
            let current = null;
            let firstNid = null;
            els.forEach(el => {
                const nid = el.dataset.notionId;
                if (!nid) return;
                if (!firstNid) firstNid = nid;
                if (el.getBoundingClientRect().top <= OFFSET) current = nid;
            });
            setActiveNotionId(current ?? firstNid);
        };
        const onScroll = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(update);
        };
        const scrollEl = mainScrollEl.current ?? window;
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        const t = setTimeout(update, 50);
        return () => {
            scrollEl.removeEventListener('scroll', onScroll);
            if (rafId) cancelAnimationFrame(rafId);
            clearTimeout(t);
        };
    }, [courseContent, selectedLevel]);

    useEffect(() => {
        const scrollEl = mainScrollEl.current;
        if (!scrollEl) return;
        const onScroll = () => setShowBackToTop(scrollEl.scrollTop > 300);
        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, []);


    // Charger les exercices du chapitre quand on est sur l'onglet exercices
    useEffect(() => {
        if (viewTab !== 'exercice' || !selectedChapter) return;
        if (chapterSkills !== null) return; // déjà chargé
        setChapterSkillsLoading(true);
        apiClient(`${API_BASE}/get_skills.php?chapter_id=${selectedChapter.id}`)
            .then(r => r.json())
            .then(d => { if (d.success) setChapterSkills(d.skills); })
            .finally(() => setChapterSkillsLoading(false));
    }, [viewTab, selectedChapter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Charger le détail d'un exercice depuis le slug URL
    useEffect(() => {
        if (!skillSlugParam || !chapterSkills) return;
        const matched = chapterSkills.find(sk => skillsSlugify(sk.title) === skillSlugParam);
        if (!matched) return;
        if (detailSkill?.id === matched.id) return; // déjà chargé
        setDetailSkillLoading(true);
        apiClient(`${API_BASE}/get_skills.php?id=${matched.id}`)
            .then(r => r.json())
            .then(d => { if (d.success) setDetailSkill(d.skill); })
            .finally(() => setDetailSkillLoading(false));
    }, [skillSlugParam, chapterSkills]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadChapter = (chapter) => {
        navigate(`/apprendre/${subjectUrlSlug(chapter)}/${generateSlug(chapter.name)}`);
    };

    const handleBackToLibrary = () => {
        if (selectedChapter) {
            navigate(`/apprendre/${subjectUrlSlug(selectedChapter)}`);
        } else {
            navigate('/apprendre', { state: { ignoreDefaultSubject: true } });
        }
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] py-20 text-center animate-fade-in relative z-10">
                <div className="relative mb-6">
                    <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-600 dark:border-t-cyan-400 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        {chapterSlugParam ? (
                            <BookOpen className="w-6 h-6 text-blue-500 dark:text-cyan-400 animate-pulse" />
                        ) : (
                            <Sparkles className="w-6 h-6 text-blue-500 dark:text-cyan-400 animate-pulse" />
                        )}
                    </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {chapterSlugParam ? 'Préparation de votre cours...' : "Chargement d'Axiomus..."}
                </h3>
            </div>
        );
    }

    if (selectedChapter && courseContent) {
        const sidebarEntries = Object.entries(courseContent).sort(sortByNotionNumber).filter(([, sections]) =>
            selectedLevel === null ||
            sections[0]?.level_number === selectedLevel ||
            sections[0]?.level_number === String(selectedLevel)
        ).map(([, sections]) => ({
            id: sections[0]?.notion_id,
            number: sections[0]?.notion_number ?? '',
            name: (sections[0]?.notion_name ?? '').replace(/^\d+(\.\d+)?\s+/, ''),
            headings: sections
                .filter(s => (s.content_type ?? s.type) === 'heading' && !/^#{3,}\s/.test(s.content || ''))
                .map(s => ({ id: s.id, text: (s.content || '').replace(/^#{1,6}\s+/, '') })),
        })).filter(n => n.id);

        const isStaff = ['admin', 'supertuteur', 'tuteur'].includes(user?.role);
        const levelsEx = levels.map((lvl, i) => {
            const mastery = Number(lvl.mastery_overall ?? 0);
            const prevMastery = i > 0 ? Number(levels[i - 1].mastery_overall ?? 0) : 0;
            const isComplete = mastery >= masteryThreshold;
            const isAccessible = isStaff || i === 0 || prevMastery >= masteryThreshold;
            const fillPct = isAccessible ? (isComplete ? 100 : mastery) : 0;
            return { ...lvl, isComplete, isAccessible, mastery, fillPct };
        });

        return (
            <>
                <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">

                    {/* ── Barre de progression par niveaux ── */}
                    {(() => {
                        return (
                            <div className="w-full mb-6 flex gap-3">
                                {levelsEx.map((lvl, index) => {
                                    const isSelected = lvl.level_number === selectedLevel;
                                    const label = `Niv. ${lvl.level_number}`;
                                    const barGradient = lvl.isComplete
                                        ? 'from-emerald-500 to-cyan-500'
                                        : lvl.fillPct >= 40
                                            ? 'from-blue-500 to-cyan-500'
                                            : 'from-blue-400 to-indigo-500';

                                    const showPadlockAfter = !isStaff && levelsEx[index + 1] && !levelsEx[index + 1].isAccessible;

                                    // Determine locked state and styling
                                    const isCurrentAccessible = lvl.isAccessible;

                                    let isClickable = false;
                                    let padlockStyle = "";
                                    let padlockTooltip = "";

                                    if (isCurrentAccessible) {
                                        isClickable = true;
                                        padlockStyle = "bg-blue-500/10 hover:bg-blue-600 border border-blue-500/20 hover:border-blue-600 text-blue-600 hover:text-white shadow-md shadow-blue-500/5 hover:shadow-blue-600/25 hover:scale-110 cursor-pointer";
                                        padlockTooltip = `Passer le test de positionnement du ${label} pour débloquer le niveau suivant`;
                                    } else {
                                        isClickable = false;
                                        padlockStyle = "text-gray-300 dark:text-gray-600 bg-gray-50/30 dark:bg-white/[0.01] border border-gray-100 dark:border-white/[0.02] cursor-not-allowed opacity-50";
                                        padlockTooltip = "Complétez d'abord les niveaux précédents pour débloquer";
                                    }

                                    return (
                                        <React.Fragment key={lvl.level_number}>
                                            <div className="flex-1 min-w-0">
                                                {/* Label + état */}
                                                <div className="flex flex-col mb-1">
                                                    <div className="flex items-center justify-between gap-1">
                                                        <button
                                                            disabled={!lvl.isAccessible}
                                                            onClick={() => { if (lvl.isAccessible && lvl.level_number !== selectedLevel) { setSelectedLevel(lvl.level_number); loadLevelContent(selectedChapter.id, lvl.level_number); } }}
                                                            title={!lvl.isAccessible ? 'Complétez le niveau précédent pour débloquer' : `Afficher ${label}${lvl.school_year ? ` (${lvl.school_year})` : ''}`}
                                                            className={`text-xs font-extrabold tracking-wider truncate transition-colors ${isSelected ? 'text-blue-600 dark:text-cyan-400' :
                                                                    lvl.isComplete ? 'text-emerald-600 dark:text-emerald-400' :
                                                                        lvl.isAccessible ? 'text-gray-600 dark:text-gray-400 hover:text-blue-500' :
                                                                            'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                                                }`}
                                                        >
                                                            {label}
                                                        </button>
                                                        {!isStaff && lvl.isComplete && (
                                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 shrink-0 text-emerald-500">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    {lvl.school_year && (
                                                        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium select-none">
                                                            {lvl.school_year}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Barre de progression bicolore — étudiants uniquement */}
                                                {!isStaff && (() => {
                                                    return (
                                                        <button
                                                            disabled={!lvl.isAccessible}
                                                            onClick={() => lvl.isAccessible && setMasteryHistoryLevel({ levelId: lvl.level_id, levelLabel: label })}
                                                            title={lvl.isAccessible ? 'Voir la progression dans le temps' : ''}
                                                            className={`w-full flex items-center gap-2 ${lvl.isAccessible ? 'cursor-pointer' : 'cursor-default'}`}
                                                        >
                                                            <div className={`relative w-full h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-white/[0.06] ${isSelected ? 'ring-1 ring-blue-400/40' : ''}`}>
                                                                <div
                                                                    className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 bg-gradient-to-r ${barGradient}`}
                                                                    style={{ width: `${lvl.fillPct}%` }}
                                                                />
                                                            </div>
                                                        </button>
                                                    );
                                                })()}
                                            </div>

                                            {showPadlockAfter && (
                                                <div className="flex items-center justify-center self-end pb-1 px-1 shrink-0">
                                                    <button
                                                        disabled={!isClickable}
                                                        onClick={() => {
                                                            if (isClickable) {
                                                                setPlacementTestLevelIndex(index);
                                                                setIsPlacementTestOpen(true);
                                                            }
                                                        }}
                                                        title={padlockTooltip}
                                                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${padlockStyle}`}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                            <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        );
                    })()}

                    {/* ── Onglets : Leçon / Exercices / Plus ── */}
                    {(() => {
                        const chapterBase = `/apprendre/${subjectUrlSlug(selectedChapter)}/${selectedChapter.slug || generateSlug(selectedChapter.name)}`;
                        const isStaffUser = ['admin', 'supertuteur'].includes(user?.role);
                        const tabs = [
                            {
                                id: 'lecon', label: 'Leçon',
                                icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" /></svg>
                            },
                            {
                                id: 'exercice', label: 'Exercices',
                                icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>
                            },
                            {
                                id: 'plus', label: 'Aller plus loin',
                                icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                            },
                        ];
                        const showModifier = isStaffUser && skillSlugParam && detailSkill && !themeSlugParam;
                        const showRetour = !!themeSlugParam && !!skillSlugParam;
                        const editUrl = showModifier ? `${chapterBase}/exercice/${skillSlugParam}/edit` : null;
                        return (
                            <div className="flex items-center justify-between gap-3 mb-6">
                                <TabBar
                                    tabs={tabs}
                                    active={!viewTab || viewTab === 'lecon' ? 'lecon' : viewTab}
                                    onChange={id => navigate(`${chapterBase}/${id}`)}
                                />
                                <div className="flex items-center gap-2">
                                    {showRetour && (
                                        <ActionBtn
                                            variant="default"
                                            onClick={() => navigate(`${chapterBase}/exercice/${skillSlugParam}`)}
                                            icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>}
                                        >
                                            Retour
                                        </ActionBtn>
                                    )}
                                </div>
                                {showModifier && (
                                    <ActionBtn
                                        variant="edit"
                                        onClick={() => navigate(editUrl, {
                                            state: {
                                                skillTitle: detailSkill.title,
                                                fromUrl: location.pathname,
                                                subjectName: selectedChapter.subject_name,
                                                chapterName: selectedChapter.name,
                                                subjectUrl: `/apprendre/${subjectUrlSlug(selectedChapter)}`,
                                                chapterUrl: chapterBase,
                                                exercicesUrl: `${chapterBase}/exercice`,
                                            }
                                        })}
                                        icon={<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>}
                                    >
                                        Modifier
                                    </ActionBtn>
                                )}
                            </div>
                        );
                    })()}

                    {/* ── Onglet Exercices ── */}
                    {viewTab === 'exercice' && (() => {
                        const chapterBase = `/apprendre/${subjectUrlSlug(selectedChapter)}/${selectedChapter.slug || generateSlug(selectedChapter.name)}`;
                        if (chapterSkillsLoading) return (
                            <div className="flex justify-center py-16">
                                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        );
                        if (themeSlugParam && detailSkill) {
                            const activeExercise = detailSkill.exercises?.find((ex, idx) => exerciseThemeSlug(ex, idx) === themeSlugParam);
                            if (activeExercise) return (
                                <SkillExercise
                                    skill={detailSkill}
                                    exercise={activeExercise}
                                    hideBackButton
                                    hideHeader
                                    onBack={() => navigate(`${chapterBase}/exercice/${skillSlugParam}`)}
                                />
                            );
                        }
                        if (skillSlugParam) {
                            if (detailSkillLoading || !detailSkill) return (
                                <div className="flex justify-center py-16">
                                    <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                                </div>
                            );
                            return (
                                <SkillDetail
                                    skill={detailSkill}
                                    user={user}
                                    editUrl={`${chapterBase}/exercice/${skillSlugParam}/edit`}
                                    hideHeader
                                    onStartExercise={(skill, ex, idx) => navigate(
                                        `${chapterBase}/exercice/${skillSlugParam}/${exerciseThemeSlug(ex, idx)}`,
                                        { state: { exerciseId: ex.id } }
                                    )}
                                />
                            );
                        }
                        const isAdminUser = ['admin', 'supertuteur'].includes(user?.role);
                        const newExerciceState = {
                            openNew: true,
                            subjectName: selectedChapter.subject_name,
                            chapterName: selectedChapter.name,
                            subjectUrl: `/apprendre/${subjectUrlSlug(selectedChapter)}`,
                            chapterUrl: chapterBase,
                            exercicesUrl: `${chapterBase}/exercice`,
                        };
                        if (!chapterSkills || chapterSkills.length === 0) return (
                            <div className="text-center py-16 text-gray-400">
                                <PencilLine className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                                <p className="font-semibold mb-3">Aucun exercice disponible pour ce chapitre.</p>
                                {isAdminUser && (
                                    <ActionBtn variant="add"
                                        onClick={() => navigate(`${chapterBase}/exercice/nouveau/edit`, { state: newExerciceState })}
                                        icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" /></svg>}>
                                        Nouvel exercice
                                    </ActionBtn>
                                )}
                            </div>
                        );
                        return (
                            <>
                                <SkillsList
                                    compact
                                    overrideSkills={chapterSkills}
                                    isAdmin={isAdminUser}
                                    onGoToSkill={sk => navigate(
                                        `${chapterBase}/exercice/${skillsSlugify(sk.title)}`,
                                        { state: { skillId: sk.id } }
                                    )}
                                />
                                {isAdminUser && (
                                    <div className="mt-6 flex justify-start">
                                        <ActionBtn variant="add"
                                            onClick={() => navigate(`${chapterBase}/exercice/nouveau/edit`, { state: newExerciceState })}
                                            icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" /></svg>}>
                                            Nouvel exercice
                                        </ActionBtn>
                                    </div>
                                )}
                            </>
                        );
                    })()}

                    {/* ── Onglet Pour aller plus loin ── */}
                    {viewTab === 'plus' && (
                        <div className="flex flex-col items-center py-20 text-center gap-4 text-gray-400">
                            <Trophy className="w-12 h-12 text-amber-500 dark:text-amber-400 animate-pulse" />
                            <p className="font-semibold text-lg text-gray-600 dark:text-gray-300">Défis</p>
                            <p className="text-sm">Les défis seront bientôt disponibles pour ce chapitre.</p>
                        </div>
                    )}

                    {/* ── Onglet Leçon ── */}
                    {(!viewTab || viewTab === 'lecon') && (
                        <>
                            {isLevelLoading ? (
                                <div className="flex justify-center py-16">
                                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : null}
                            {!isLevelLoading && (
                                <CourseContentDisplay
                                    courseContent={courseContent}
                                    selectedLevel={selectedLevel}
                                    user={user}
                                    lockedNotions={lockedNotions}
                                    masteryThreshold={masteryThreshold}
                                    notionScores={notionScores}
                                    chapters={chapters}
                                    loadChapter={loadChapter}
                                    openItemsNotionId={openItemsNotionId}
                                    setOpenItemsNotionId={setOpenItemsNotionId}
                                    sidebarEntries={sidebarEntries}
                                    activeNotionId={activeNotionId}
                                    setActiveNotionId={setActiveNotionId}
                                    mainScrollEl={mainScrollEl}
                                    renderNotionHeaderActions={['admin', 'supertuteur'].includes(user?.role) ? (notionName, firstSection, isLocked) => {
                                        return firstSection?.notion_name && (
                                            <ActionBtn
                                                variant="edit"
                                                onClick={() => {
                                                    const url = `/apprendre/${subjectUrlSlug(selectedChapter)}/${selectedChapter.slug || generateSlug(selectedChapter.name)}/notion/${generateSlug(firstSection.notion_name)}/edit`;
                                                    navigate(url);
                                                }}
                                                icon={
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                    </svg>
                                                }
                                                title="Modifier cette notion dans l'interface admin"
                                            >
                                                Modifier
                                            </ActionBtn>
                                        );
                                    } : null}
                                    renderSectionActions={['admin', 'supertuteur'].includes(user?.role) ? (section) => (
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log("Edit button clicked for section:", section);
                                                setEditingSection(section);
                                            }}
                                            className="absolute top-2.5 right-2.5 z-[999] p-1.5 rounded-lg bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 shadow-sm transition-all hover:scale-105 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                                            title="Modifier ce bloc"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600 dark:text-gray-300">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                            </svg>
                                        </button>
                                    ) : null}
                                />
                            )}
                        </>
                    )}
                </div>
                {masteryHistoryLevel && (
                    <LevelMasteryModal
                        levelId={masteryHistoryLevel.levelId}
                        levelLabel={masteryHistoryLevel.levelLabel}
                        onClose={() => setMasteryHistoryLevel(null)}
                    />
                )}

                <button
                    onClick={() => mainScrollEl.current?.scrollTo({ top: 0, behavior: 'smooth' })}
                    aria-label="Retour en haut"
                    className={`md:hidden fixed bottom-20 right-4 z-[60] w-10 h-10 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-cyan-400 hover:border-blue-300 dark:hover:border-cyan-700 transition-all duration-200 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
                        }`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                    </svg>
                </button>
                {isPlacementTestOpen && (
                    <PlacementTest
                        chapterId={selectedChapter.id}
                        levels={levels}
                        user={user}
                        initialLevelIndex={placementTestLevelIndex}
                        onClose={() => setIsPlacementTestOpen(false)}
                        onSuccess={reloadActiveChapter}
                    />
                )}
                {renderEditingSectionModal()}
            </>
        );
    }

    // Chapitre sélectionné mais vide (aucun niveau/contenu)
    if (selectedChapter && !courseContent && !isLoading) {
        const isAdminUser = ['admin', 'supertuteur'].includes(user?.role);
        const chapterBase = `/apprendre/${subjectUrlSlug(selectedChapter)}/${selectedChapter.slug || generateSlug(selectedChapter.name)}`;
        const tabs = [
            { id: 'lecon', label: 'Leçon' },
            { id: 'exercice', label: 'Exercices' },
            { id: 'plus', label: 'Aller plus loin' },
        ];
        return (
            <>
                <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">
                    <div className="flex items-center justify-between gap-3 mb-6">
                        <TabBar tabs={tabs} active={!viewTab || viewTab === 'lecon' ? 'lecon' : viewTab}
                            onChange={id => navigate(`${chapterBase}/${id}`)} />
                    </div>
                    <div className="text-center py-16 text-gray-400">
                        <BookOpen className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                        <p className="font-semibold text-gray-500 dark:text-gray-400 mb-2">Ce chapitre est vide pour le moment.</p>
                        {isAdminUser && (
                            <button onClick={() => navigate(`${chapterBase}/edit`)}
                                className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-500 hover:text-blue-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                </svg>
                                Ajouter du contenu
                            </button>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // Grouper les chapitres par Matière (subject_name) puis par Thème (theme_name)
    const groupedChapters = chapters.reduce((acc, chapter) => {
        const subject = chapter.subject_name || 'Général';
        const theme = chapter.theme_name || 'Général';
        if (!acc[subject]) acc[subject] = {};
        if (!acc[subject][theme]) acc[subject][theme] = [];
        acc[subject][theme].push(chapter);
        return acc;
    }, {});

    if (subjectSlugParam) {
        const subjectName = Object.keys(groupedChapters).find(s => {
            const chaptersForSubject = Object.values(groupedChapters[s]).flat();
            const slug = chaptersForSubject[0]?.subject_slug;
            return slug ? slug === subjectSlugParam : generateSlug(s) === subjectSlugParam;
        });

        if (subjectName && groupedChapters[subjectName]) {
            const themes = groupedChapters[subjectName];
            const subjectId = Object.values(themes).flat()[0]?.subject_id ?? null;
            const isAdminUser = ['admin', 'supertuteur'].includes(user?.role);
            return (
                <>
                    <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">
                        <div className="space-y-8">
                            <div className="bg-white/40 dark:bg-black/10 rounded-3xl p-6 md:p-8 border border-gray-200/50 dark:border-white/[0.02]">
                                <div className="space-y-10">
                                    {Object.entries(themes).map(([theme, themeChapters]) => (
                                        <div key={theme}>
                                            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-300 mb-6 flex items-center gap-2">
                                                <span className="w-8 h-[2px] bg-gray-300 dark:bg-gray-700"></span>
                                                {theme}
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {themeChapters.map(chapter => {
                                                    const mastery = chapter.mastery || {};
                                                    const overall = mastery.overall ?? 0;
                                                    const levelLabel = chapter.current_level_label ?? null;
                                                    const lastActivity = mastery.last_activity_at;
                                                    const daysSince = lastActivity
                                                        ? Math.floor((Date.now() - new Date(lastActivity).getTime()) / 86400000)
                                                        : null;
                                                    const freshnessColor = daysSince === null ? 'bg-gray-300 dark:bg-gray-600'
                                                        : daysSince <= 7 ? 'bg-emerald-400'
                                                            : daysSince <= 30 ? 'bg-amber-400'
                                                                : 'bg-red-400';
                                                    const barColor = overall >= 80 ? 'from-emerald-500 to-cyan-500'
                                                        : overall >= 40 ? 'from-blue-500 to-cyan-500'
                                                            : 'from-blue-400 to-indigo-500';
                                                    return (
                                                        <div key={chapter.id} onClick={() => loadChapter(chapter)} className="group bg-white/60 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] rounded-xl p-4 cursor-pointer hover:border-blue-400/50 dark:hover:border-blue-500/40 hover:bg-white dark:hover:bg-white/[0.04] transition-colors flex flex-col justify-between h-full">
                                                            <div className="flex items-start justify-between gap-2 mb-3">
                                                                <h4 className="text-sm font-semibold text-gray-800 dark:text-white leading-snug">{chapter.name}</h4>
                                                                {!['admin', 'supertuteur', 'tuteur'].includes(user?.role) && !!chapter.has_locked_notions && (
                                                                    <span className="shrink-0 p-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex items-center justify-center">
                                                                        <Lock className="w-3 h-3" />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {!['admin', 'supertuteur', 'tuteur'].includes(user?.role) && (
                                                                <div>
                                                                    <div className="flex justify-between items-center text-xs text-gray-400 dark:text-gray-500 mb-1">
                                                                        <span className="flex items-center gap-1.5">
                                                                            {daysSince !== null && (
                                                                                <span title={daysSince <= 7 ? 'Récent' : daysSince <= 30 ? 'À réviser bientôt' : 'Non pratiqué depuis longtemps'}
                                                                                    className={`w-1.5 h-1.5 rounded-full inline-block ${freshnessColor}`} />
                                                                            )}
                                                                            {levelLabel && <span className="text-[10px] font-medium text-blue-500 dark:text-blue-400">{levelLabel}</span>}
                                                                        </span>
                                                                        <span className={`font-semibold ${overall >= 80 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>{overall}%</span>
                                                                    </div>
                                                                    <div className="w-full bg-gray-100 dark:bg-white/[0.05] rounded-full h-1 overflow-hidden"
                                                                        title={overall > 0 ? `Notions : ${mastery.notion ?? 0}% · Exercices : ${mastery.exercice ?? 0}%` : undefined}>
                                                                        <div className={`bg-gradient-to-r ${barColor} h-1 rounded-full transition-all duration-700`} style={{ width: `${overall}%` }} />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {isAdminUser && (
                                    <div className="mt-8 pt-6 border-t border-gray-200/60 dark:border-white/[0.05]">
                                        <button onClick={() => setAdminModal({ type: 'chapter', subjectId, subjectName })}
                                            className="group flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-xl hover:border-blue-400 dark:hover:border-blue-500 hover:bg-white/40 dark:hover:bg-white/[0.03] transition-colors text-sm font-medium text-gray-400 hover:text-blue-500">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                            </svg>
                                            Nouveau chapitre
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {adminModal?.type === 'chapter' && (
                        <ChapterFormModal
                            subjectId={adminModal.subjectId}
                            subjectName={adminModal.subjectName}
                            busy={adminBusy}
                            onClose={() => setAdminModal(null)}
                            onSave={async (data) => {
                                setAdminBusy(true);
                                try {
                                    const res = await apiClient(`${API_BASE}/admin_content.php?action=save_chapter`, {
                                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(data),
                                    });
                                    const json = await res.json();
                                    if (json.success) { reloadChapters(); setAdminModal(null); }
                                    else alert(json.message || 'Erreur lors de la création.');
                                } finally { setAdminBusy(false); }
                            }}
                        />
                    )}
                </>
            );
        } else if (!isLoading) {
            // Admins: check if subject exists but has no chapters yet
            const isAdminUser = ['admin', 'supertuteur'].includes(user?.role);
            const emptySubject = isAdminUser ? allSubjects.find(s => (s.slug || generateSlug(s.name)) === subjectSlugParam) : null;
            if (emptySubject) {
                return (
                    <>
                        <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">
                            <div className="bg-white/40 dark:bg-black/10 rounded-3xl p-6 md:p-8 border border-gray-200/50 dark:border-white/[0.02]">
                                <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Aucun chapitre dans cette matière.</p>
                                <button onClick={() => setAdminModal({ type: 'chapter', subjectId: emptySubject.id, subjectName: emptySubject.name })}
                                    className="group flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-xl hover:border-blue-400 dark:hover:border-blue-500 hover:bg-white/40 dark:hover:bg-white/[0.03] transition-colors text-sm font-medium text-gray-400 hover:text-blue-500">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                        <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                    </svg>
                                    Nouveau chapitre
                                </button>
                            </div>
                        </div>
                        {adminModal?.type === 'chapter' && (
                            <ChapterFormModal
                                subjectId={adminModal.subjectId}
                                subjectName={adminModal.subjectName}
                                busy={adminBusy}
                                onClose={() => setAdminModal(null)}
                                onSave={async (data) => {
                                    setAdminBusy(true);
                                    try {
                                        const res = await apiClient(`${API_BASE}/admin_content.php?action=save_chapter`, {
                                            method: 'POST', headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify(data),
                                        });
                                        const json = await res.json();
                                        if (json.success) { reloadChapters(); setAdminModal(null); }
                                        else alert(json.message || 'Erreur lors de la création.');
                                    } finally { setAdminBusy(false); }
                                }}
                            />
                        )}
                        {renderEditingSectionModal()}
                    </>
                );
            }
            return (
                <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">
                    <button onClick={handleBackToLibrary} className="mb-6 text-gray-500 hover:text-gray-700 dark:hover:text-white transition-colors">← Retour aux matières</button>
                    <div className="text-center py-10 text-gray-500">Matière introuvable.</div>
                </div>
            );
        }
    }

    const isAdminUser = ['admin', 'supertuteur'].includes(user?.role);
    // For admins: all subjects (including empty ones); for students: subjects derived from chapters
    const subjectsForGrid = isAdminUser && allSubjects.length > 0
        ? allSubjects.map(s => ({ name: s.name, slug: s.slug || generateSlug(s.name), chapterCount: Object.values(groupedChapters[s.name] ?? {}).flat().length }))
        : Object.keys(groupedChapters).map(name => {
            const themeCount = Object.keys(groupedChapters[name]).length;
            const chaptersForSubject = Object.values(groupedChapters[name]).flat();
            const slug = chaptersForSubject[0]?.subject_slug || generateSlug(name);
            return { name, slug, themeCount };
        });

    return (
        <>
            <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[60vh]">
                {isLoading ? <div className="text-center py-10">Chargement des matières...</div> : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {subjectsForGrid.map(subjectObj => (
                            <div key={subjectObj.name} onClick={() => navigate(`/apprendre/${subjectObj.slug}`)} className="group bg-white/60 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] rounded-xl p-4 cursor-pointer hover:border-blue-400/50 dark:hover:border-blue-500/40 hover:bg-white dark:hover:bg-white/[0.04] transition-colors flex flex-col items-center text-center gap-3">
                                <div className="bg-blue-100 dark:bg-blue-900/30 w-10 h-10 rounded-xl flex items-center justify-center">
                                    <SubjectIcon subject={subjectObj.name} className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-white">{subjectObj.name}</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        {isAdminUser
                                            ? `${subjectObj.chapterCount} chapitre${subjectObj.chapterCount !== 1 ? 's' : ''}`
                                            : `${subjectObj.themeCount} thème${subjectObj.themeCount > 1 ? 's' : ''}`}
                                    </p>
                                </div>
                            </div>
                        ))}
                        {isAdminUser && (
                            <button onClick={() => setAdminModal({ type: 'subject' })}
                                className="group border-2 border-dashed border-gray-300 dark:border-white/10 rounded-xl p-4 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-white/40 dark:hover:bg-white/[0.03] transition-colors flex flex-col items-center justify-center text-center gap-2 min-h-[96px]">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-white/[0.05] group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors">
                                        <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                    </svg>
                                </div>
                                <p className="text-xs font-medium text-gray-400 group-hover:text-blue-500 transition-colors">Nouvelle matière</p>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {adminModal?.type === 'subject' && (
                <SubjectFormModal
                    busy={adminBusy}
                    onClose={() => setAdminModal(null)}
                    onSave={async (data) => {
                        setAdminBusy(true);
                        try {
                            const res = await apiClient(`${API_BASE}/admin_content.php?action=save_subject`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(data),
                            });
                            const json = await res.json();
                            if (json.success) { reloadChapters(); setAdminModal(null); }
                            else alert(json.message || 'Erreur lors de la création.');
                        } finally { setAdminBusy(false); }
                    }}
                />
            )}
            {adminModal?.type === 'chapter' && (
                <ChapterFormModal
                    subjectId={adminModal.subjectId}
                    subjectName={adminModal.subjectName}
                    busy={adminBusy}
                    onClose={() => setAdminModal(null)}
                    onSave={async (data) => {
                        setAdminBusy(true);
                        try {
                            const res = await apiClient(`${API_BASE}/admin_content.php?action=save_chapter`, {
                                method: 'POST', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(data),
                            });
                            const json = await res.json();
                            if (json.success) { reloadChapters(); setAdminModal(null); }
                            else alert(json.message || 'Erreur lors de la création.');
                        } finally { setAdminBusy(false); }
                    }}
                />
            )}
            {renderEditingSectionModal()}
        </>
    );
}

const SCHOOL_YEARS_CV = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];

function AdminFormModal({ title, onClose, onSave, busy, children }) {
    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={title}
        >
            <div className="space-y-4">
                {children}
            </div>
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-white/[0.04] mt-6">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">Annuler</button>
                <button onClick={onSave} disabled={busy} className="px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl disabled:opacity-50 transition-colors">
                    {busy ? 'Enregistrement…' : 'Enregistrer'}
                </button>
            </div>
        </Modal>
    );
}

function AdminField({ label, children }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">{label}</label>
            {children}
        </div>
    );
}

function AdminInput({ value, onChange, placeholder, type = 'text' }) {
    return (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition" />
    );
}

function AdminSelect({ value, onChange, options, placeholder }) {
    return (
        <select value={value ?? ''} onChange={e => onChange(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition">
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

function SubjectFormModal({ busy, onClose, onSave }) {
    const [name, setName] = useState('');
    const autoSlug = name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return (
        <AdminFormModal title="Nouvelle matière" onClose={onClose} onSave={() => onSave({ name, slug: autoSlug })} busy={busy}>
            <AdminField label="Nom de la matière">
                <AdminInput value={name} onChange={setName} placeholder="ex: Mathématiques" />
            </AdminField>
            <AdminField label="Slug URL (auto)">
                <p className="text-xs text-gray-400 dark:text-gray-500 font-mono bg-gray-50 dark:bg-white/[0.04] px-3 py-2 rounded-xl">{autoSlug || '…'}</p>
            </AdminField>
        </AdminFormModal>
    );
}


function ChapterFormModal({ subjectId, subjectName, busy, onClose, onSave }) {
    const [f, setF] = useState({ name: '', display_group: '', school_year: '', description: '', subject_id: subjectId });
    const set = k => v => setF(p => ({ ...p, [k]: v }));
    return (
        <AdminFormModal title={`Nouveau chapitre${subjectName ? ` — ${subjectName}` : ''}`} onClose={onClose} onSave={() => onSave(f)} busy={busy}>
            <AdminField label="Nom du chapitre">
                <AdminInput value={f.name} onChange={set('name')} placeholder="ex: Calcul littéral" />
            </AdminField>
            <AdminField label="Groupe / Thème">
                <AdminInput value={f.display_group} onChange={set('display_group')} placeholder="ex: Algèbre" />
            </AdminField>
            <AdminField label="Année de départ (niveau 1)">
                <AdminSelect value={f.school_year} onChange={set('school_year')} placeholder="— Choisir —"
                    options={SCHOOL_YEARS_CV.map(y => ({ value: y, label: y }))} />
            </AdminField>
            <AdminField label="Description (optionnel)">
                <AdminInput value={f.description} onChange={set('description')} placeholder="Courte description…" />
            </AdminField>
        </AdminFormModal>
    );
}

export default CourseView;